import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { Subject } from 'rxjs';
import { DataTableDirective } from 'angular-datatables';
import { ToastrService } from 'ngx-toastr';
import { SellosFirmasService } from 'src/app/services/configuraciones/sellos-firmas.service';

interface UsuarioFirma {
  id: number;
  nombre: string;
  usuario: string;
  perfil: string;
  estado: string;
  sello_url: string | null;
  firma_p12_url: string | null;
  datos_adicionales: string | null;
  firma_actualizada: string | null;
}

@Component({
    selector: 'app-sellos-firmas',
    templateUrl: './sellos-firmas.component.html',
    styleUrls: ['./sellos-firmas.component.scss'],
    standalone: false
})
export class SellosFirmasComponent implements OnInit, OnDestroy {
  private readonly maxSelloBytes = 5 * 1024 * 1024;
  private readonly maxP12Bytes = 2 * 1024 * 1024;

  @ViewChild(DataTableDirective, { static: false })
  dtElement!: DataTableDirective;

  dtOptions: DataTables.Settings = {};
  dtTrigger: Subject<any> = new Subject();

  public cargando = false;
  public guardando = false;

  public usuarios: UsuarioFirma[] = [];
  public usuarioSeleccionado: UsuarioFirma | null = null;

  /* form */
  public clave_p12 = '';
  public datos_adicionales = '';
  public sello_url = '';
  public firma_p12_url = '';

  /* estado de subida */
  public subiendoSello = false;
  public subiendoFirma = false;

  private token = '';
  private tablaInicializada = false;

  constructor(
    private sellosFirmasService: SellosFirmasService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.token = localStorage.getItem('token') || '';
    this.dtOptions = {
      language: {
        processing: 'Procesando...',
        search: 'Buscar:',
        lengthMenu: 'Mostrar _MENU_ registros',
        info: 'Mostrando desde _START_ al _END_ de _TOTAL_ elementos',
        infoEmpty: 'Mostrando ningún elemento.',
        infoFiltered: '(filtrado _MAX_ elementos total)',
        loadingRecords: 'Cargando registros...',
        zeroRecords: 'No se encontraron registros',
        emptyTable: 'No hay datos disponibles en la tabla',
        paginate: { first: 'Primero', previous: 'Ant', next: 'Sig', last: 'Último' },
        aria: {
          sortAscending: ': Activar para ordenar la tabla en orden ascendente',
          sortDescending: ': Activar para ordenar la tabla en orden descendente'
        }
      },
      pagingType: 'full_numbers',
      pageLength: 10,
      responsive: true
    };
    this.cargarUsuarios();
  }

  ngOnDestroy(): void {
    this.dtTrigger.unsubscribe();
  }

  cargarUsuarios(): void {
    this.cargando = true;
    this.sellosFirmasService.listarUsuarios().subscribe({
      next: res => {
        this.cargando = false;
        this.usuarios = res?.rows || [];

        if (this.tablaInicializada) {
          this.dtElement.dtInstance.then((dtInstance: DataTables.Api) => {
            dtInstance.destroy();
            this.tablaInicializada = false;
            setTimeout(() => {
              this.dtTrigger.next(null);
              this.tablaInicializada = true;
            });
          });
        } else {
          setTimeout(() => {
            this.dtTrigger.next(null);
            this.tablaInicializada = true;
          });
        }
      },
      error: () => {
        this.cargando = false;
        this.toastr.error('No se pudo cargar la lista de usuarios');
      }
    });
  }

  seleccionarUsuario(usuario: UsuarioFirma): void {
    this.usuarioSeleccionado = usuario;
    this.sello_url = usuario.sello_url || '';
    this.firma_p12_url = usuario.firma_p12_url || '';
    this.datos_adicionales = usuario.datos_adicionales || '';
    this.clave_p12 = '';

    this.sellosFirmasService.obtenerConfiguracion(usuario.id).subscribe({
      next: res => {
        if (res?.row) {
          this.sello_url = res.row.sello_url || '';
          this.firma_p12_url = res.row.firma_p12_url || '';
          this.datos_adicionales = res.row.datos_adicionales || '';
        }
      },
      error: () => this.toastr.error('No se pudo cargar la configuración del usuario')
    });
  }

  /* ------------------------------------------------------------------ */
  /*  Subida de imagen del sello                                         */
  /* ------------------------------------------------------------------ */
  onSelloChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    const ALLOWED = ['image/jpeg', 'image/png', 'image/webp'];
    if (!ALLOWED.includes(file.type)) {
      this.toastr.warning('Solo se permiten imágenes JPG, PNG o WEBP');
      input.value = '';
      return;
    }

    if (file.size > this.maxSelloBytes) {
      this.toastr.warning('El sello no debe superar los 5 MB');
      input.value = '';
      return;
    }

    this.subiendoSello = true;
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result as string;
      this.sellosFirmasService.uploadFile(base64, file.name, file.type).subscribe({
        next: res => {
          this.subiendoSello = false;
          if (res?.url) {
            this.sello_url = res.url;
            this.toastr.success('Sello subido correctamente');
          } else {
            this.toastr.error('Error al subir el sello');
          }
        },
        error: () => {
          this.subiendoSello = false;
          this.toastr.error('Error al subir el sello');
        }
      });
    };
    reader.readAsDataURL(file);
  }

  /* ------------------------------------------------------------------ */
  /*  Subida de archivo .p12                                             */
  /* ------------------------------------------------------------------ */
  onFirmaP12Change(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.p12') && file.type !== 'application/x-pkcs12') {
      this.toastr.warning('Solo se permite archivo con extensión .p12');
      input.value = '';
      return;
    }

    if (file.size > this.maxP12Bytes) {
      this.toastr.warning('La firma .p12 no debe superar los 2 MB');
      input.value = '';
      return;
    }

    this.subiendoFirma = true;
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result as string;
      this.sellosFirmasService.uploadFile(base64, file.name, 'application/x-pkcs12').subscribe({
        next: res => {
          this.subiendoFirma = false;
          if (res?.url) {
            this.firma_p12_url = res.url;
            this.toastr.success('Firma .p12 subida correctamente');
          } else {
            this.toastr.error('Error al subir la firma .p12');
          }
        },
        error: () => {
          this.subiendoFirma = false;
          this.toastr.error('Error al subir la firma .p12');
        }
      });
    };
    reader.readAsDataURL(file);
  }

  /* ------------------------------------------------------------------ */
  /*  Guardar configuración                                              */
  /* ------------------------------------------------------------------ */
  guardar(): void {
    if (!this.usuarioSeleccionado) return;

    this.guardando = true;
    const datos: any = {
      sello_url: this.sello_url || null,
      firma_p12_url: this.firma_p12_url || null,
      datos_adicionales: this.datos_adicionales || null
    };
    if (this.clave_p12) {
      datos.clave_p12 = this.clave_p12;
    }

    this.sellosFirmasService.guardarConfiguracion(this.usuarioSeleccionado.id, datos).subscribe({
      next: () => {
        this.guardando = false;
        this.toastr.success('Configuración guardada correctamente');
        this.cancelar();
      },
      error: () => {
        this.guardando = false;
        this.toastr.error('Error al guardar la configuración');
      }
    });
  }

  cancelar(): void {
    this.tablaInicializada = false;
    this.usuarioSeleccionado = null;
    this.sello_url = '';
    this.firma_p12_url = '';
    this.clave_p12 = '';
    this.datos_adicionales = '';
    this.cargarUsuarios();
  }
}
